import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT } from "@/lib/pnp";
import { Phone, Mail, Instagram, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";
import { RevealWords, Rise } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Danke! Wir melden uns schnellstmöglich zurück.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte per Telefon oder WhatsApp melden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-[#082E19] text-white py-24 md:py-40 overflow-hidden"
    >
      <div className="px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-7">
            <div className="font-mono-pp text-[11px] uppercase tracking-[0.24em] text-[#17C964] mb-6">
              Kontakt · Kostenlose Besichtigung
            </div>
            <h2
              className="font-display font-extrabold leading-[0.9] tracking-tighter"
              style={{ fontSize: "clamp(2.5rem, 8vw, 7rem)" }}
            >
              <RevealWords text="Reden wir über Ihren Garten." />
            </h2>

            <div className="mt-10 md:mt-14 grid grid-cols-12 gap-5">
              <a
                href={CONTACT.telHref}
                data-testid="contact-phone-link"
                className="col-span-12 md:col-span-6 group flex items-center justify-between gap-4 rounded-md ring-1 ring-white/15 p-5 hover:bg-white hover:text-[#082E19] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-11 w-11 rounded-full items-center justify-center bg-[#17C964] text-[#082E19]">
                    <Phone size={17} />
                  </span>
                  <div>
                    <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-70">Anrufen</div>
                    <div className="font-display font-semibold text-lg leading-tight">{CONTACT.phone}</div>
                  </div>
                </div>
                <ArrowUpRight size={18} />
              </a>

              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="col-span-12 md:col-span-6 group flex items-center justify-between gap-4 rounded-md ring-1 ring-white/15 p-5 hover:bg-white hover:text-[#082E19] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-11 w-11 rounded-full items-center justify-center bg-[#17C964] text-[#082E19]">
                    <MessageCircle size={17} />
                  </span>
                  <div>
                    <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-70">WhatsApp</div>
                    <div className="font-display font-semibold text-lg leading-tight">{CONTACT.phone}</div>
                  </div>
                </div>
                <ArrowUpRight size={18} />
              </a>

              <a
                href={CONTACT.emailHref}
                data-testid="contact-email-link"
                className="col-span-12 md:col-span-6 group flex items-center justify-between gap-4 rounded-md ring-1 ring-white/15 p-5 hover:bg-white hover:text-[#082E19] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-11 w-11 rounded-full items-center justify-center bg-[#17C964] text-[#082E19]">
                    <Mail size={17} />
                  </span>
                  <div>
                    <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-70">E-Mail</div>
                    <div className="font-display font-semibold text-lg leading-tight break-all">{CONTACT.email}</div>
                  </div>
                </div>
                <ArrowUpRight size={18} className="shrink-0" />
              </a>

              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="col-span-12 md:col-span-6 group flex items-center justify-between gap-4 rounded-md ring-1 ring-white/15 p-5 hover:bg-white hover:text-[#082E19] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-11 w-11 rounded-full items-center justify-center bg-[#17C964] text-[#082E19]">
                    <Instagram size={17} />
                  </span>
                  <div>
                    <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-70">Instagram</div>
                    <div className="font-display font-semibold text-lg leading-tight">@pundpgartenservice</div>
                  </div>
                </div>
                <ArrowUpRight size={18} />
              </a>

              <div className="col-span-12 flex items-center gap-3 mt-2 text-white/80">
                <MapPin size={16} className="text-[#17C964]" />
                <span className="font-mono-pp text-[11px] uppercase tracking-[0.22em]">
                  {CONTACT.serviceArea}
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="col-span-12 md:col-span-5">
            <Rise>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white text-[#082E19] rounded-md p-6 md:p-8 ring-1 ring-white/10"
              >
                <div className="font-mono-pp text-[11px] uppercase tracking-[0.22em] text-[#4F6858]">
                  Anfrage · Kostenlos & unverbindlich
                </div>
                <div className="mt-6 space-y-2">
                  <label className="font-mono-pp text-[10px] uppercase tracking-[0.22em] text-[#4F6858]">Name</label>
                  <input
                    data-testid="contact-input-name"
                    className="pp-input"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Ihr Name"
                    required
                  />
                </div>
                <div className="mt-6 space-y-2">
                  <label className="font-mono-pp text-[10px] uppercase tracking-[0.22em] text-[#4F6858]">E-Mail</label>
                  <input
                    data-testid="contact-input-email"
                    className="pp-input"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="mail@beispiel.de"
                    required
                  />
                </div>
                <div className="mt-6 space-y-2">
                  <label className="font-mono-pp text-[10px] uppercase tracking-[0.22em] text-[#4F6858]">Telefon (optional)</label>
                  <input
                    data-testid="contact-input-phone"
                    className="pp-input"
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="015…"
                  />
                </div>
                <div className="mt-6 space-y-2">
                  <label className="font-mono-pp text-[10px] uppercase tracking-[0.22em] text-[#4F6858]">Nachricht</label>
                  <textarea
                    data-testid="contact-input-message"
                    className="pp-input resize-none"
                    rows={4}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Erzählen Sie uns kurz, was Ihr Garten braucht."
                    required
                  />
                </div>
                <motion.button
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  className="mt-8 pp-btn pp-btn-primary w-full justify-center disabled:opacity-60"
                >
                  {loading ? "Wird gesendet…" : "Anfrage senden →"}
                </motion.button>
                <p className="mt-3 text-[11px] text-[#4F6858] font-mono-pp uppercase tracking-widest">
                  Kostenlose Besichtigung & Angebot
                </p>
              </form>
            </Rise>
          </div>
        </div>
      </div>
    </section>
  );
}
